import { InstallationReportingMduReportContext } from '../../../entities/installation-reporting-mdu-details.entities';
import {
  BuildingSectionResponse,
  BuildingSectionsResponse,
  FileMetaV2Response,
  SectionV2Response,
} from '../model/installation-reporting-mdu-bff.entities';
import {
  InstallationReportSectionConfig,
  InstallationReportSectionId,
} from '../model/installation-reporting-mdu-section.types';
import { DeliveryPointSectionsResponse } from './delivery-point-section-state';
import {
  emptyItemAnswer,
  emptySectionState,
  mapSectionResponseToSectionState,
} from './installation-reporting-mdu-section-state.mapper';
import { SectionState } from './installation-reporting-mdu-section-state.types';

export type ReportSectionStates = Partial<
  Record<InstallationReportSectionId, SectionState>
>;

type SectionResponse = BuildingSectionResponse | SectionV2Response;
type ResponsesBySection = Partial<
  Record<InstallationReportSectionId, SectionResponse | undefined>
>;

const buildingResponsesBySection = (
  sections: BuildingSectionsResponse,
): ResponsesBySection => ({
  PLOT_DESIGN: sections.plotDesign,
  PRE_INSTALLATION: sections.preInstallation,
  PLOT_EXCAVATION: sections.plotExcavation,
  DUCT_CONNECTING: sections.ductConnection,
  CABLE_BLOWING: sections.cableBlowing,
  SPLICING_MEASURING: sections.splicingMeasuring,
  COMMISSION: sections.commissioning,
});

const deliveryPointResponsesBySection = (
  sections: DeliveryPointSectionsResponse,
): ResponsesBySection => ({
  PRE_INSTALLATION: sections.preInstallation,
  SPLICING_MEASURING: sections.splicingMeasuring,
  COMMISSION: sections.commissioning,
});

export const responsesBySection = (
  reportType: InstallationReportingMduReportContext['reportType'],
  sections: BuildingSectionsResponse | DeliveryPointSectionsResponse,
): ResponsesBySection =>
  reportType === 'BUILDING'
    ? buildingResponsesBySection(sections as BuildingSectionsResponse)
    : deliveryPointResponsesBySection(
        sections as DeliveryPointSectionsResponse,
      );

/** After upload/mark-done, keep the full checklist visible instead of "show controlled only". */
const shouldShowAllItemsAfterChange = (
  section: InstallationReportSectionConfig,
  state: SectionState,
  focusDefinitionId?: string,
): boolean =>
  !!focusDefinitionId &&
  section.items.some((item) => item.id === focusDefinitionId) &&
  !!state.itemAnswers[focusDefinitionId]?.completed;

const sectionStateAfterReload = (
  section: InstallationReportSectionConfig,
  response: SectionResponse | undefined,
  currentState: SectionState,
  focusDefinitionId?: string,
): SectionState => {
  const mapped = response
    ? mapSectionResponseToSectionState(section, response, currentState)
    : currentState;
  return shouldShowAllItemsAfterChange(section, mapped, focusDefinitionId)
    ? { ...mapped, showControlled: false }
    : mapped;
};

export const mergeReloadedSectionStates = (
  sections: InstallationReportSectionConfig[],
  responses: ResponsesBySection,
  priorStates: ReportSectionStates | null,
  initialStates: ReportSectionStates,
  focusDefinitionId?: string,
): ReportSectionStates =>
  Object.fromEntries(
    sections.map((section) => [
      section.id,
      sectionStateAfterReload(
        section,
        responses[section.id],
        priorStates?.[section.id] ??
          initialStates[section.id] ??
          emptySectionState(section),
        focusDefinitionId,
      ),
    ]),
  );

/** Marks an item as completed with its freshly uploaded attachments. */
export const withUploadedSelfInspection = (
  sections: InstallationReportSectionConfig[],
  states: ReportSectionStates | null,
  definitionId: string,
  attachments: FileMetaV2Response[],
): ReportSectionStates | null => {
  const section = sections.find((config) =>
    config.items.some((item) => item.id === definitionId),
  );
  const current = section && states?.[section.id];
  if (!section || !states || !current) {
    return null;
  }
  return {
    ...states,
    [section.id]: {
      ...current,
      itemAnswers: {
        ...current.itemAnswers,
        [definitionId]: {
          ...(current.itemAnswers[definitionId] ?? emptyItemAnswer()),
          completed: true,
          attachments,
        },
      },
    },
  };
};
